import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
// ⚠️ apiClient, NOT axiosClient. Every notification route is in hrms at
// /api/notifications; axiosClient points at the auth-service, which has no such
// route. Using it 404'd all ten calls — and because the push setup swallows its
// own errors on purpose ("the bell still works"), the failure was silent: the UI
// reported notifications as ON while no push subscription had ever been created,
// so the server pushed to nobody and closing the browser meant hearing nothing.
import { useAuth, apiClient } from '../auth-lib';
import { createChatSocket } from '../lib/chatSocket';
import {
  showOsToast, setAppBadge, permissionState, requestPermission,
  registerServiceWorker, subscribeToPush, unsubscribeFromPush, canPush,
} from '../lib/notifications';

// One source of truth for the bell, the sidebar dots and the OS toast.
//
// All three read the same `counts` object, which is why there is a provider at
// all rather than a hook per consumer: the bell in the top bar and the dot on
// the Tasks entry must never disagree, and two independent fetches would drift
// the moment one of them re-ran.
//
// ⚠️ STAFF ONLY. It mounts inside the authenticated staff tree and opens a
// 'staff' socket. The client portal has its own auth context, its own token and
// its own socket kind, and deliberately shares nothing here — see the note in
// models/Notification.js about why portal notifications would get their own
// collection rather than a filter on this one.

const NotificationContext = createContext(null);

const EMPTY = { total: 0, sections: {} };

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(EMPTY);
  const [permission, setPermission] = useState(permissionState());
  const [loading, setLoading] = useState(false);
  // Cursor paging state. `nextBefore` is the createdAt of the oldest row held,
  // which is what the server pages on — an offset would duplicate rows as new
  // notifications arrive at the head mid-scroll.
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // ── The live-refresh signal ────────────────────────────────────────────────
  //
  // A counter every data-loading page can depend on, so the app stops needing a
  // manual refresh to show work that arrived while you were looking at it.
  //
  // It lives here because this provider already owns the only always-on socket
  // in the staff app — putting it anywhere else would mean a second connection
  // whose whole purpose is to learn the same things this one already hears.
  //
  // ⚠️ A COUNTER, NOT A DATA CHANNEL. It says "something changed, re-ask the
  // server", never what changed. Pushing actual rows down this path would mean
  // every page needing a reducer for every event, and a page showing state the
  // server never confirmed. Refetching is a request; it is also always correct.
  const [revision, setRevision] = useState(0);

  // Throttled, because the window-focus trigger below fires on every alt-tab.
  // Somebody moving between the app and Excel every few seconds must not launch
  // a fetch storm across every mounted page.
  const lastBump = useRef(0);
  const bumpRevision = useCallback((minGapMs = 0) => {
    const now = Date.now();
    if (minGapMs && now - lastBump.current < minGapMs) return;
    lastBump.current = now;
    setRevision((r) => r + 1);
  }, []);

  // The socket, kept in a ref so the effect that owns its lifetime doesn't
  // re-run when a notification arrives and changes state.
  const socketRef = useRef(null);

  // `navigate` identity changes on every route change; the socket handlers
  // close over it and must not force a reconnect each time somebody clicks a
  // link. A ref keeps the handler stable while still calling the current one.
  const navRef = useRef(navigate);
  useEffect(() => { navRef.current = navigate; }, [navigate]);

  const openNotification = useCallback((n) => {
    // Read first, then navigate: the row is being dealt with, and doing it in
    // this order means the badge is already correct when the page paints.
    if (!n.readAt) {
      apiClient.post('/api/notifications/read', { ids: [n.id] }).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      setCounts((prev) => {
        const next = Math.max(0, (prev.sections[n.section] || 0) - 1);
        return {
          total: Math.max(0, prev.total - 1),
          sections: { ...prev.sections, [n.section]: next },
        };
      });
    }
    if (n.path) navRef.current(n.path);
  }, []);

  const openRef = useRef(openNotification);
  useEffect(() => { openRef.current = openNotification; }, [openNotification]);

  // ── Initial load ───────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [feed, tally] = await Promise.all([
        apiClient.get('/api/notifications', { params: { limit: 20 } }),
        apiClient.get('/api/notifications/counts'),
      ]);
      setItems(feed.data?.notifications || []);
      setHasMore(!!feed.data?.hasMore);
      setNextBefore(feed.data?.nextBefore || null);
      setCounts(tally.data || EMPTY);
    } catch {
      // A failed fetch leaves the last known state rather than blanking the
      // bell — a transient 401 during token refresh must not read as "you have
      // nothing to do".
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Live delivery ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return undefined;

    // A second socket alongside the chat page's own — genuinely second, not
    // multiplexed: socket.io's `io()` cache treats a repeat call for a URL and
    // namespace it has already handed out as a request for a NEW Manager
    // (`sameNamespace` forces `newConnection`), so this opens its own transport.
    //
    // That is what makes the cleanup below safe — `disconnect()` here cannot
    // close the socket the chat page is using — at the cost of one extra
    // connection per user while they sit on a chat page. Worth it: sharing one
    // socket would couple the bell's lifetime to the chat page's, so navigating
    // away from chat would silently stop notifications arriving.
    const socket = createChatSocket('staff');
    socketRef.current = socket;

    socket.on('notify:new', (n) => {
      setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev].slice(0, 50)));
      showOsToast(n, (clicked) => openRef.current(clicked));
      // A notification means something in the data actually changed — a task
      // was assigned, a request was raised. Whichever page is open re-asks.
      bumpRevision();
    });

    socket.on('notify:counts', (c) => setCounts(c || EMPTY));

    // A dropped connection means missed events. Re-fetching on reconnect is
    // what stops a laptop coming out of sleep with a stale badge — the socket
    // reconnects silently and would otherwise deliver nothing, since the events
    // it missed were emitted to a room it was not in at the time.
    socket.on('connect', () => {
      refresh();
      // Reconnecting means we were away and missed events. Whatever is on
      // screen is now of unknown age, so every page re-asks.
      bumpRevision();
    });

    return () => {
      socket.off('notify:new');
      socket.off('notify:counts');
      socket.off('connect');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, refresh, bumpRevision]);

  // Coming back to the tab.
  //
  // The socket covers everything that happens while the app is open, but not
  // what a COLLEAGUE changed directly in the database, or anything that fires no
  // notification at all — a status edit, a comment, a completed subtask. Nobody
  // reads a screen they left an hour ago and wonders whether it is stale; they
  // just believe it. Re-asking on return is what makes that belief true.
  //
  // 20 seconds between bumps: enough that flicking to Excel and back does not
  // refetch, short enough that a real return to the app always does.
  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => {
      if (document.visibilityState === 'visible') bumpRevision(20000);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [user, bumpRevision]);

  // Taskbar badge follows the unread total. Harmless no-op unless installed.
  useEffect(() => { setAppBadge(counts.total); }, [counts.total]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !nextBefore) return;
    setLoadingMore(true);
    try {
      const res = await apiClient.get('/api/notifications', {
        params: { limit: 20, before: nextBefore },
      });
      const page = res.data?.notifications || [];
      // De-duplicated on append: a live `notify:new` can land between the
      // request and its response, and an id already at the head must not
      // reappear further down the list.
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...page.filter((n) => !seen.has(n.id))];
      });
      setHasMore(!!res.data?.hasMore);
      setNextBefore(res.data?.nextBefore || null);
    } catch {
      /* leave the list as it stands; the button remains for a retry */
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, nextBefore]);

  const markAllRead = useCallback(async (section = null) => {
    try {
      const res = await apiClient.post('/api/notifications/read-all', section ? { section } : {});
      setCounts(res.data || EMPTY);
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => (
        (!section || n.section === section) && !n.readAt ? { ...n, readAt: now } : n
      )));
    } catch {
      /* leave state alone; the next refresh corrects it */
    }
  }, []);

  /**
   * Turn on desktop notifications: permission, then a push subscription.
   *
   * The two are separate steps and both are needed. Permission alone gets a
   * toast only while a tab is open; the push subscription is what lets the
   * service worker be woken with every tab closed. Doing them together means
   * the user answers one question rather than two.
   */
  const enableDesktop = useCallback(async () => {
    const result = await requestPermission();
    setPermission(result);
    if (result !== 'granted') return result;

    try {
      await registerServiceWorker();
      const { data } = await apiClient.get('/api/notifications/push/config');
      if (data?.enabled && data.publicKey) {
        const sub = await subscribeToPush(data.publicKey);
        if (sub) await apiClient.post('/api/notifications/push/subscribe', sub);
      }
    } catch {
      // Push failed to set up — permission is still granted, so the in-tab
      // toast works. Deliberately not surfaced as an error: the user asked for
      // notifications and got most of them.
    }
    return result;
  }, []);

  /**
   * Push to this user's own browsers now, and report what actually happened.
   *
   * ⚠️ "Permission granted" IS NOT "subscribed", and conflating them is what let
   * a broken subscribe call hide for as long as it did — the browser grants
   * permission locally, without ever contacting us, so every screen said
   * notifications were on while the server had nobody to push to. This asks the
   * server, which is the only party that knows.
   */
  const testDesktop = useCallback(async () => {
    const { data } = await apiClient.post('/api/notifications/push/test');
    return data;
  }, []);

  const disableDesktop = useCallback(async () => {
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) await apiClient.post('/api/notifications/push/unsubscribe', { endpoint });
    } catch {
      /* the browser-side unsubscribe is what matters; the row is pruned on
         its next 410 anyway */
    }
  }, []);

  // Re-subscribe on load for somebody who already granted permission.
  //
  // A push subscription can lapse without the user touching anything — Chrome
  // rotates endpoints, and clearing site data drops it — while the permission
  // survives. Without this, they would stay permanently "on" in the UI and
  // silently receive nothing, with no prompt to fix it because the enable
  // button only shows while permission is 'default'.
  useEffect(() => {
    if (!user || !canPush() || permissionState() !== 'granted') return;
    (async () => {
      try {
        await registerServiceWorker();
        const { data } = await apiClient.get('/api/notifications/push/config');
        if (!data?.enabled || !data.publicKey) return;
        const sub = await subscribeToPush(data.publicKey);
        if (sub) await apiClient.post('/api/notifications/push/subscribe', sub);
      } catch {
        /* best effort — the bell still works */
      }
    })();
  }, [user]);

  const value = useMemo(() => ({
    testDesktop,
    items,
    counts,
    loading,
    permission,
    hasMore,
    loadingMore,
    loadMore,
    revision,
    bumpRevision,
    refresh,
    openNotification,
    markAllRead,
    enableDesktop,
    disableDesktop,
    // Sugar for the sidebar, so a nav entry asks for its own dot by name
    // instead of every caller reaching into `counts.sections`.
    countFor: (section) => counts.sections[section] || 0,
  }), [
    items, counts, loading, permission, hasMore, loadingMore, loadMore,
    revision, bumpRevision,
    refresh, openNotification, markAllRead, enableDesktop, disableDesktop, testDesktop,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Returns an inert shape rather than throwing when there is no provider above.
 *
 * The cockpit layouts are also rendered in the admin and portal trees where
 * this provider does not mount, and a hard throw would turn "no notifications
 * here" into a blank screen. A zero count renders as no badge, which is exactly
 * right for those trees.
 */
export function useNotifications() {
  return useContext(NotificationContext) || {
    items: [],
    counts: EMPTY,
    loading: false,
    permission: 'unsupported',
    hasMore: false,
    loadingMore: false,
    loadMore: () => {},
    // 0 forever outside the provider: a page using useLiveRefresh in the portal
    // tree simply never re-fetches on a signal, which is correct there.
    revision: 0,
    bumpRevision: () => {},
    refresh: () => {},
    openNotification: () => {},
    markAllRead: () => {},
    enableDesktop: async () => 'unsupported',
    disableDesktop: async () => {},
    countFor: () => 0,
  };
}

export default NotificationContext;
